import { useEffect, useState, useRef } from 'react';
import { useRownd } from '@rownd/react';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapConfig';
import { Box, Button, Paper, TextField, Typography, ButtonGroup, CircularProgress, Alert, Card, CardContent } from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';

// Define custom popup styles
const popupStyle = `
.mapboxgl-popup {
  max-width: 400px;
  font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif;
}

.mapboxgl-popup-content {
  padding: 15px;
  width: 280px; 
}

.popup-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 5px;
}

.popup-row {
  margin-bottom: 5px;
}

.popup-label {
  font-weight: bold;
}
`;

// Inject styles
const injectCustomStyles = () => {
  const style = document.createElement('style');
  style.innerHTML = popupStyle;
  document.head.appendChild(style);
};

// API Base URL
const API_BASE_URL = 'https://tsunami-data.robert-9e7.workers.dev/api';

// Predefined locations
const LOCATIONS = {
  tokyo: { center: [139.6503, 35.6762], zoom: 4, label: 'Tokyo, Japan' },
  manila: { center: [120.9842, 14.5995], zoom: 4, label: 'Manila, Philippines' },
  singapore: { center: [103.8198, 1.3521], zoom: 4, label: 'Singapore' },
  sydney: { center: [151.2093, -33.8688], zoom: 4, label: 'Sydney, Australia' }
};

const TSUNAMI_DATA = [
  {
    timestamp: '2024-04-12 04:18:00',
    event_type: 'Earthquake',
    location_name: 'Epicenter',
    latitude: 16.65541,
    longitude: 116.096248,
    wave_height_m: 0,
    distance_km: 0,
    notes: 'Initial M8.3 earthquake detected'
  },
  {
    timestamp: '2024-04-12 04:20:55',
    event_type: 'Buoy Detection',
    location_name: 'Buoy-Central',
    latitude: 16.5,
    longitude: 116.4,
    wave_height_m: 3.43,
    distance_km: 36.7,
    notes: 'Wave detected by Buoy-Central'
  },
  {
    timestamp: '2024-04-12 04:26:21',
    event_type: 'Buoy Detection',
    location_name: 'Buoy-W01',
    latitude: 16.4,
    longitude: 115.3,
    wave_height_m: 1.8,
    distance_km: 89.51,
    notes: 'Wave detected by Buoy-W01'
  },
  {
    timestamp: '2024-04-12 04:27:00',
    event_type: 'Detection',
    location_name: 'Tsunami Warning Center',
    latitude: 16.85541,
    longitude: 116.246248,
    wave_height_m: 0.2,
    distance_km: 25,
    notes: 'Tsunami confirmed, warnings issued'
  }
];

// Vessel data extracted from image
const VESSEL_DATA = [
  { id: 'CV-001', name: 'MV Thomas', lastCheckIn: '2025-04-10 16:41', status: 'Diverted', location: { lat: 14.82, lng: 123.85 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-002', name: 'MV Gibson', lastCheckIn: '2025-04-10 14:56', status: 'Diverted', location: { lat: 15.027, lng: 120.55 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-003', name: 'MV Townsend', lastCheckIn: '2025-04-10 14:02', status: 'Awaiting Tow', location: { lat: 13.192, lng: 124.665 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-004', name: 'MV Thornton', lastCheckIn: '2025-04-10 15:04', status: 'Damaged', location: { lat: 14.28, lng: 123.84 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-005', name: 'MV Dennis', lastCheckIn: '2025-04-10 16:24', status: 'Damaged', location: { lat: 13.754, lng: 121.735 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-006', name: 'MV Hamilton', lastCheckIn: '2025-04-10 16:08', status: 'Diverted', location: { lat: 16.824, lng: 122.777 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-007', name: 'MV Marsh', lastCheckIn: '2025-04-10 14:43', status: 'Damaged', location: { lat: 16.426, lng: 123.192 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-008', name: 'MV Patel', lastCheckIn: '2025-04-10 12:47', status: 'Diverted', location: { lat: 13.6, lng: 124.257 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-010', name: 'MV Bryant', lastCheckIn: '2025-04-10 12:29', status: 'Diverted', location: { lat: 13.506, lng: 122.77 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-011', name: 'MV Williams', lastCheckIn: '2025-04-10 12:44', status: 'Damaged', location: { lat: 15.869, lng: 123.875 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-012', name: 'MV Fitzpatrick', lastCheckIn: '2025-04-10 15:27', status: 'Anchored', location: { lat: 15.361, lng: 122.641 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-013', name: 'MV Valentine', lastCheckIn: '2025-04-10 14:50', status: 'Diverted', location: { lat: 14.607, lng: 120.31 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-015', name: 'MV Parker', lastCheckIn: '2025-04-10 14:12', status: 'In Transit', location: { lat: 16.204, lng: 124.508 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-016', name: 'MV Tate', lastCheckIn: '2025-04-10 17:26', status: 'Anchored', location: { lat: 15.341, lng: 121.634 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-017', name: 'MV Harris', lastCheckIn: '2025-04-10 15:35', status: 'Anchored', location: { lat: 15.784, lng: 123.589 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-019', name: 'MV Lopez', lastCheckIn: '2025-04-10 13:05', status: 'Awaiting Tow', location: { lat: 14.335, lng: 120.063 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-020', name: 'MV Reed', lastCheckIn: '2025-04-10 16:17', status: 'In Transit', location: { lat: 13.476, lng: 122.978 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-022', name: 'MV Richmond', lastCheckIn: '2025-04-10 12:26', status: 'Diverted', location: { lat: 15.239, lng: 120.194 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-023', name: 'MV Clark', lastCheckIn: '2025-04-10 17:30', status: 'Diverted', location: { lat: 16.492, lng: 120.236 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-024', name: 'MV Blanchard', lastCheckIn: '2025-04-10 15:48', status: 'Damaged', location: { lat: 13.377, lng: 120.7 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-025', name: 'MV Peters', lastCheckIn: '2025-04-10 16:53', status: 'Awaiting Tow', location: { lat: 13.267, lng: 124.729 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-026', name: 'MV Santana', lastCheckIn: '2025-04-10 13:19', status: 'Diverted', location: { lat: 14.897, lng: 121.718 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-029', name: 'MV Wong', lastCheckIn: '2025-04-10 14:30', status: 'Awaiting Tow', location: { lat: 16.367, lng: 120.475 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-031', name: 'MV Smith', lastCheckIn: '2025-04-10 17:36', status: 'Operational', location: { lat: 16.969, lng: 122.792 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-032', name: 'MV Foster', lastCheckIn: '2025-04-10 17:51', status: 'Anchored', location: { lat: 13.32, lng: 121.49 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-033', name: 'MV Ayala', lastCheckIn: '2025-04-10 12:35', status: 'Operational', location: { lat: 16.413, lng: 124.105 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-034', name: 'MV Knight', lastCheckIn: '2025-04-10 12:11', status: 'Diverted', location: { lat: 13.135, lng: 123.852 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-035', name: 'MV Lee', lastCheckIn: '2025-04-10 17:13', status: 'Operational', location: { lat: 15.772, lng: 124.262 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-036', name: 'MV Randall', lastCheckIn: '2025-04-10 13:12', status: 'Damaged', location: { lat: 13.636, lng: 123.485 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-038', name: 'MV Young', lastCheckIn: '2025-04-10 14:07', status: 'Damaged', location: { lat: 13.85, lng: 120.805 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-039', name: 'MV West', lastCheckIn: '2025-04-10 13:14', status: 'Awaiting Tow', location: { lat: 13.293, lng: 122.288 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-040', name: 'MV Little', lastCheckIn: '2025-04-10 14:12', status: 'In Transit', location: { lat: 16.423, lng: 122.522 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-043', name: 'MV Spencer', lastCheckIn: '2025-04-10 17:32', status: 'Diverted', location: { lat: 14.665, lng: 122.83 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-044', name: 'MV Schmitt', lastCheckIn: '2025-04-10 12:03', status: 'Operational', location: { lat: 14.175, lng: 123.365 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-045', name: 'MV Lynch', lastCheckIn: '2025-04-10 16:40', status: 'Damaged', location: { lat: 16.857, lng: 122.773 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-046', name: 'MV Love', lastCheckIn: '2025-04-10 14:52', status: 'Anchored', location: { lat: 15.263, lng: 123.577 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-047', name: 'MV Hampton', lastCheckIn: '2025-04-10 12:25', status: 'Diverted', location: { lat: 16.37, lng: 120.58 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-048', name: 'MV Hunter', lastCheckIn: '2025-04-10 13:54', status: 'Awaiting Tow', location: { lat: 13.907, lng: 123.844 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-050', name: 'MV Garza', lastCheckIn: '2025-04-10 17:10', status: 'Damaged', location: { lat: 13.878, lng: 124.428 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-051', name: 'MV Rice', lastCheckIn: '2025-04-10 14:56', status: 'In Transit', location: { lat: 14.91, lng: 122.462 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-052', name: 'MV Cooper', lastCheckIn: '2025-04-10 12:12', status: 'In Transit', location: { lat: 15.353, lng: 120.147 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-053', name: 'MV Dickerson', lastCheckIn: '2025-04-10 17:01', status: 'Anchored', location: { lat: 13.735, lng: 124.664 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-056', name: 'MV Juarez', lastCheckIn: '2025-04-10 14:11', status: 'In Transit', location: { lat: 13.768, lng: 120.441 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-057', name: 'MV Cervantes', lastCheckIn: '2025-04-10 12:24', status: 'In Transit', location: { lat: 13.614, lng: 120.96 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-058', name: 'MV Zamora', lastCheckIn: '2025-04-10 17:11', status: 'Awaiting Tow', location: { lat: 15.415, lng: 121.572 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-059', name: 'MV Pearson', lastCheckIn: '2025-04-10 16:59', status: 'Diverted', location: { lat: 15.231, lng: 124.525 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-060', name: 'MV King', lastCheckIn: '2025-04-10 13:30', status: 'Damaged', location: { lat: 14.889, lng: 122.998 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-061', name: 'MV Boone', lastCheckIn: '2025-04-10 13:18', status: 'Diverted', location: { lat: 16.9, lng: 120.037 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-062', name: 'MV Blankenship', lastCheckIn: '2025-04-10 14:18', status: 'Diverted', location: { lat: 13.62, lng: 122.179 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-064', name: 'MV Wang', lastCheckIn: '2025-04-10 14:35', status: 'Awaiting Tow', location: { lat: 14.336, lng: 123.636 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-065', name: 'MV Palmer', lastCheckIn: '2025-04-10 13:30', status: 'Damaged', location: { lat: 13.21, lng: 122.08 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-066', name: 'MV Snyder', lastCheckIn: '2025-04-10 16:54', status: 'Damaged', location: { lat: 16.151, lng: 122.362 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-067', name: 'MV Aguilar', lastCheckIn: '2025-04-10 12:17', status: 'In Transit', location: { lat: 13.954, lng: 124.744 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-068', name: 'MV Thomas', lastCheckIn: '2025-04-10 15:03', status: 'Operational', location: { lat: 14.059, lng: 122.983 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-069', name: 'MV Lowe', lastCheckIn: '2025-04-10 17:17', status: 'Awaiting Tow', location: { lat: 13.166, lng: 123.28 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-070', name: 'MV Callahan', lastCheckIn: '2025-04-10 14:01', status: 'In Transit', location: { lat: 15.474, lng: 124.203 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-073', name: 'MV McCarthy', lastCheckIn: '2025-04-10 17:06', status: 'In Transit', location: { lat: 13.846, lng: 120.385 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-075', name: 'MV Williams', lastCheckIn: '2025-04-10 15:04', status: 'Diverted', location: { lat: 15.765, lng: 122.978 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-076', name: 'MV Larson', lastCheckIn: '2025-04-10 12:02', status: 'Awaiting Tow', location: { lat: 16.663, lng: 123.48 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-077', name: 'MV Robinson', lastCheckIn: '2025-04-10 12:42', status: 'Anchored', location: { lat: 13.633, lng: 121.545 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-078', name: 'MV Collins', lastCheckIn: '2025-04-10 14:13', status: 'Operational', location: { lat: 13.018, lng: 121.347 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-079', name: 'MV Howell', lastCheckIn: '2025-04-10 17:05', status: 'Awaiting Tow', location: { lat: 15.512, lng: 124.827 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-080', name: 'MV Curtis', lastCheckIn: '2025-04-10 15:33', status: 'Anchored', location: { lat: 16.079, lng: 122.765 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-081', name: 'MV Goodman', lastCheckIn: '2025-04-10 17:34', status: 'Diverted', location: { lat: 15.738, lng: 124.549 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-082', name: 'MV Carroll', lastCheckIn: '2025-04-10 17:09', status: 'Diverted', location: { lat: 15.454, lng: 124.447 }, safeHarbor: 'Awaiting Assignment' },
  { id: 'CV-084', name: 'MV Smith', lastCheckIn: '2025-04-10 17:35', status: 'Diverted', location: { lat: 14.327, lng: 122.033 }, safeHarbor: 'Awaiting Assignment' }
];

// List of cities with coordinates
const CITIES = [
  { id: 'CITY-001', name: 'Port Salinera', location: { lat: 16.04, lng: 120.3 } },
  { id: 'CITY-002', name: 'Coralspire City', location: { lat: 15.2, lng: 120.6 } },
  { id: 'CITY-003', name: 'New Dovarra', location: { lat: 16.4, lng: 120.6 } },
  { id: 'CITY-004', name: 'Lakara Gulf Point', location: { lat: 13.154189, lng: 123.742415 } },
  { id: 'CITY-005', name: 'Delmaris Township', location: { lat: 15.5, lng: 120.6 } }
];

// List of ports with coordinates
const PORTS = [
  { id: 'PORT-001', name: 'Salinera Port', location: { lat: 16.042832, lng: 120.333973 } },
  { id: 'PORT-002', name: 'Lakara Gulf Point Port', location: { lat: 13.145098, lng: 123.758405 } }
];

// Create a constant list of vessel IDs that are in danger - removing CV-061
const VESSELS_IN_DANGER = [
  'CV-023', 'CV-013', 'CV-019', 'CV-073', 
  'CV-038', 'CV-057', 'CV-056', 'CV-077', 'CV-032', 
  'CV-062', 'CV-039', 'CV-065', 'CV-005', 'CV-020', 
  'CV-010', 'CV-069'
];

// List of airports with coordinates
const AIRPORTS = [
  { id: 'AIRPORT-001', name: 'NARIN INTL AIRPORT', code: 'NIA', location: { lat: 16.04, lng: 120.24 } },
  { id: 'AIRPORT-002', name: 'BAYLOR AERIAL TERMINAL', code: 'BAT', location: { lat: 15.19, lng: 120.56 } },
  { id: 'AIRPORT-003', name: 'OSTRA FLIGHT STRIP', code: 'OFS', location: { lat: 13.11, lng: 123.68 } }
];

function SimpleMapTest() {
  const { is_authenticated, is_initializing, requestSignIn, getAccessToken } = useRownd();
  const [map, setMap] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customLat, setCustomLat] = useState('');
  const [customLng, setCustomLng] = useState('');
  const [customZoom, setCustomZoom] = useState('10');
  const [selectedTsunamiPoint, setSelectedTsunamiPoint] = useState(null);
  const [showVessels, setShowVessels] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [airportPoints, setAirportPoints] = useState([]);
  const [showAirports, setShowAirports] = useState(true);
  const [circlePosition, setCirclePosition] = useState('')
  const [circleRadius, setCircleRadius] = useState(20); 
  const [circleLatitude, setCircleLatitude] = useState(39.8283); 
  const [circleLongitude, setCircleLongitude] = useState(-98.5795); 

  // State for custom popup
  const [selectedReport, setSelectedReport] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const mapContainer = useRef(null);

  // Change map view to specific location
  const flyToLocation = (longitude, latitude, zoom = 10) => {
    if (map) {
      map.flyTo({
        center: [longitude, latitude],
        zoom: zoom,
        essential: true,
        duration: 2000 // Animation time in milliseconds
      });
    }
  };

  // Add epicenter location
  const TSUNAMI_EPICENTER = {
    center: [TSUNAMI_DATA[0].longitude, TSUNAMI_DATA[0].latitude],
    zoom: 4,
    label: 'Tsunami Epicenter'
  };

  // Fly to tsunami epicenter
  const flyToTsunamiEpicenter = () => {
    if (map) {
      flyToLocation(TSUNAMI_EPICENTER.center[0], TSUNAMI_EPICENTER.center[1], TSUNAMI_EPICENTER.zoom);
    }
  };

  // Fly to predefined location
  const flyToPredefinedLocation = (locationKey) => {
    const location = LOCATIONS[locationKey];
    if (location && map) {
      flyToLocation(location.center[0], location.center[1], location.zoom);
    }
  };

  // Fly to custom location
  const flyToCustomLocation = () => {
    const lat = parseFloat(customLat);
    const lng = parseFloat(customLng);
    const zoom = parseFloat(customZoom);
    
    if (!isNaN(lat) && !isNaN(lng) && !isNaN(zoom)) {
      flyToLocation(lng, lat, zoom);
    }
  };

  // Close custom popup
  const closePopup = () => {
    setSelectedReport(null);
    setSelectedTsunamiPoint(null);
    setSelectedVessel(null);
  };

  // Toggle vessel display
  const toggleVessels = () => {
    const newShowVessels = !showVessels;
    setShowVessels(newShowVessels);
    
    if (map) {
      // Update vessel layer visibility
      map.setLayoutProperty('vessels-layer', 'visibility', newShowVessels ? 'visible' : 'none');
      map.setLayoutProperty('vessels-text', 'visibility', newShowVessels ? 'visible' : 'none');
    }
  };

  // Convert reports to GeoJSON format
  const createGeoJson = (reportsArray) => {
    if (!reportsArray || reportsArray.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: reportsArray.map(report => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [report.longitude, report.latitude]
        },
        properties: {
          ...report, // Include all report properties
          id: report.id,
          title: report.title || report.text || 'Unnamed Report',
          description: report.description || report.text || 'No description',
          verified: report.verified ? 'Yes' : 'No',
        }
      }))
    };
  };
    // Update the circle position
    const updateCirclePosition = () => {
      const lat = parseFloat(circleLatitude);
      const lng = parseFloat(circleLongitude);
  
      if (!isNaN(lat) && !isNaN(lng)) {
        setCircleLatitude(lat); // Update circle latitude
        setCircleLongitude(lng); // Update circle longitude
  
        // Update the circle's source data
        if (map && map.getSource('custom-circle')) {
          map.getSource('custom-circle').setData({
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [lng, lat],
                },
              },
            ],
          });
        }
        
      }
    };
  
  // Convert airports data to GeoJSON format
  const createAirportsGeoJson = (airportsData) => {
    if (!airportsData || airportsData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: airportsData.map((airport) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [airport.location.lng, airport.location.lat]
        },
        properties: {
          ...airport,
          title: `${airport.name} (${airport.code})`,
          description: `Airport code: ${airport.code}`,
          isAirport: true
        }
      }))
    };
  };
  

  // Convert tsunami data to GeoJSON format
  const createTsunamiGeoJson = (tsunamiData) => {
    if (!tsunamiData || tsunamiData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: tsunamiData.map((point, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.longitude, point.latitude]
        },
        properties: {
          ...point,
          id: `tsunami-${index}`,
          title: point.location_name,
          description: point.notes,
          isTsunamiPoint: true
        }
      }))
    };
  };

  // Convert vessel data to GeoJSON format
  const createVesselGeoJson = (vesselData) => {
    if (!vesselData || vesselData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: vesselData.map((vessel) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [vessel.location.lng, vessel.location.lat]
        },
        properties: {
          ...vessel,
          title: vessel.name,
          description: `Status: ${vessel.status}`,
          isVessel: true
        }
      }))
    };
  };

  const refreshMapData = async () => {
    try {
      setLoading(true);
      setError(null);
  
      // Fetch the latest reports
      const reportData = await fetchReports();
  
      setReports(reportData);
  
      // Update the map with the new data
      if (map && reportData && reportData.length > 0) {
        const source = map.getSource('points-source');
        if (source) {
          source.setData(createGeoJson(reportData));
        }
      }
    } catch (error) {
      setError('Failed to refresh the data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle airport display
  const toggleAirports = () => {
    const newShowAirports = !showAirports;
    setShowAirports(newShowAirports);
    
    if (map) {
      // Update airport layer visibility
      map.setLayoutProperty('airports-layer', 'visibility', newShowAirports ? 'visible' : 'none');
      map.setLayoutProperty('airports-text', 'visibility', newShowAirports ? 'visible' : 'none');
    }
  };
  

  // Fetch reports with Rownd token
  const fetchReports = async () => {
    try {
      // Get access token from Rownd
      const accessToken = await getAccessToken({ waitForToken: is_authenticated });
      
      // Build authorization header if token exists
      const headers = accessToken 
        ? { 'Authorization': `Bearer ${accessToken}` }
        : {};

      // Make the API request
      const response = await fetch(`${API_BASE_URL}/reports/recent`, { 
        headers 
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch reports');
      }
      
      // Extract reports with valid coordinates
      const reportsArray = data.data?.reports || [];
      const validReports = reportsArray.filter(report => 
        report.latitude !== null && report.longitude !== null
      );
      
      return validReports;
    } catch (error) {
      console.error('Error fetching reports:', error);
      throw error;
    }
  };

  // Load reports data
  useEffect(() => {
    let isMounted = true;
    
    async function loadReports() {
      if (!map) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Get reports using our fetch function
        const reportData = await fetchReports();
        
        if (isMounted) {
          setReports(reportData || []);
          
          // If we have the map and reports, update the map data
          if (map && reportData && reportData.length > 0) {
            const source = map.getSource('points-source');
            if (source) {
              source.setData(createGeoJson(reportData));
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading reports:', error);
          setError('Failed to load reports. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    loadReports();
    
    return () => {
      isMounted = false;
    };
  }, [map, is_authenticated]); // Added is_authenticated to re-fetch when auth state changes

  // Initialize map
  useEffect(() => {
    // Inject custom styles
    injectCustomStyles();
    
    // Log token for debugging
    console.log('Map token:', MAPBOX_ACCESS_TOKEN);
    
    // Set token
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    
    // Create a very basic map
    const mapInstance = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [TSUNAMI_DATA[0].longitude, TSUNAMI_DATA[0].latitude], // Center on tsunami epicenter
      zoom: 4 // Zoomed out view of epicenter
    });
    
    // Save the map instance
    setMap(mapInstance);
    
    // Add load handler
    mapInstance.on('load', () => {
      console.log('Map loaded successfully');
      
      // Add GeoJSON source with reports data
      mapInstance.addSource('points-source', {
        type: 'geojson',
        data: createGeoJson(reports) // Initial empty or loaded data
      });
      
      // Add a circle layer for points but set it to not be visible
      mapInstance.addLayer({
        id: 'points-layer',
        type: 'circle',
        source: 'points-source',
        paint: {
          'circle-radius': 8,
          'circle-color': [
            'case',
            ['==', ['get', 'verified'], 'Yes'], '#4CAF50', // Green for verified
            '#FF9800' // Orange for unverified
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        },
        layout: {
          'visibility': 'none' // Hide the reports layer
        }
      });

      mapInstance.addSource('terrain', {
        type: 'raster',
        url: 'mapbox://mapbox.terrain-rgb',
        tileSize: 256
      });
  
      mapInstance.addLayer({
        id: 'terrain-layer',
        type: 'raster',
        source: 'terrain',
        paint: {
          'raster-opacity': 0.25
        }
      });
  
      // Add the DEM source before using it for terrain
      mapInstance.addSource('mapbox-dem', {
        'type': 'raster-dem',
        'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
        'tileSize': 512,
        'maxzoom': 14
      });
      
      // Only enable 3D terrain if DEM source is available
      try {
        // Optionally, enable 3D terrain
        mapInstance.setTerrain({
          source: 'mapbox-dem',
          exaggeration: 1.5 // Reduced exaggeration for better stability
        });
      } catch (error) {
        console.warn('Could not enable 3D terrain:', error);
      }
      
      // Add a symbol layer for text labels but set it to not be visible
      mapInstance.addLayer({
        id: 'points-text',
        type: 'symbol',
        source: 'points-source',
        layout: {
          'text-field': ['get', 'title'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'visibility': 'none' // Hide the reports text labels
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        }
      });

      mapInstance.addSource('custom-circle', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [circleLongitude, circleLatitude],
              },
            },
          ],
        },
      });
      
      
      // Circle for Safe Zones
      mapInstance.addLayer({
        id: 'custom-circle-layer',
        type: 'circle',
        source: 'custom-circle',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, circleRadius * 0.5, 22, circleRadius * 2],
          'circle-color': 'rgba(0, 255, 0, 0.5)',
          'circle-stroke-width': 1,
        },
      });
      

      mapInstance.on('zoom', () => {
        // Reapply the circle radius in case zoom affects it, keep it static
        mapInstance.setPaintProperty('custom-circle', 'circle-radius', circleRadius);
      });

      // Remove any existing airport-related layers and sources
      if (mapInstance.getLayer('philippine-airports-layer')) {
        mapInstance.removeLayer('philippine-airports-layer');
      }
      if (mapInstance.getLayer('philippine-airports-text')) {
        mapInstance.removeLayer('philippine-airports-text');
      }
      if (mapInstance.getSource('philippine-airports-source')) {
        mapInstance.removeSource('philippine-airports-source');
      }
      
      // Add airports data source with new AIRPORTS list
      mapInstance.addSource('airports-source', {
        type: 'geojson',
        data: createAirportsGeoJson(AIRPORTS)
      });
      
      // Add icon for airports
      mapInstance.loadImage(
        'https://img.icons8.com/ios-filled/50/000000/airport.png',
        (error, image) => {
          if (error) {
            console.error('Failed to load airport icon:', error);
            return;
          }
          
          // Add the airport image to the map
          mapInstance.addImage('airport-icon', image);
          
          // Add a symbol layer for airports
          mapInstance.addLayer({
            id: 'airports-layer',
            type: 'symbol',
            source: 'airports-source',
            layout: {
              'icon-image': 'airport-icon',
              'icon-size': 0.5,
              'icon-allow-overlap': true
            }
          });
          
          // Add a text layer for airport names
          mapInstance.addLayer({
            id: 'airports-text',
            type: 'symbol',
            source: 'airports-source',
            layout: {
              'text-field': ['get', 'code'],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 10,
              'text-offset': [0, 1.5],
              'text-anchor': 'top',
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#3f51b5',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1
            }
          });
          
          // Remove the click event for airports - no more popups
          
          // Keep the cursor change effect
          mapInstance.on('mouseenter', 'airports-layer', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
          });
          
          mapInstance.on('mouseleave', 'airports-layer', () => {
            mapInstance.getCanvas().style.cursor = '';
          });
        }
      );
      
      // Add tsunami data source
      mapInstance.addSource('tsunami-points-source', {
        type: 'geojson',
        data: createTsunamiGeoJson(TSUNAMI_DATA)
      });
      
      // Add a circle layer for tsunami points
      mapInstance.addLayer({
        id: 'tsunami-points-layer',
        type: 'circle',
        source: 'tsunami-points-source',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'wave_height_m'],
            0, 3,
            1, 5,
            3, 8
          ],
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'wave_height_m'],
            0, '#3498db',    // Blue for minimal waves
            0.5, '#2ecc71',  // Green for small waves
            1, '#f39c12',    // Orange for medium waves
            2, '#e74c3c'     // Red for large waves
          ],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.8
        }
      });
      
      // Add a symbol layer for tsunami point text labels
      mapInstance.addLayer({
        id: 'tsunami-points-text',
        type: 'symbol',
        source: 'tsunami-points-source',
        layout: {
          'text-field': ['get', 'location_name'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-offset': [0, 1.5],
          'text-anchor': 'top'
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        }
      });
      
      // Add vessel data source
      mapInstance.addSource('vessels-source', {
        type: 'geojson',
        data: createVesselGeoJson(VESSEL_DATA)
      });
      
      // Add icon for vessels
      mapInstance.loadImage(
        'https://img.icons8.com/ios-filled/50/000000/cargo-ship.png',
        (error, image) => {
          if (error) {
            console.error('Failed to load ship icon:', error);
            return;
          }
          
          // Add the ship image to the map
          mapInstance.addImage('ship-icon', image);
          
          // Add a symbol layer for vessels
          mapInstance.addLayer({
            id: 'vessels-layer',
            type: 'symbol',
            source: 'vessels-source',
            layout: {
              'visibility': 'none', // Initially hidden
              'icon-image': 'ship-icon',
              'icon-size': 0.5,
              'icon-allow-overlap': true,
              'icon-rotate': 45
            }
          });
          
          // Add a text layer for vessel names
          mapInstance.addLayer({
            id: 'vessels-text',
            type: 'symbol',
            source: 'vessels-source',
            layout: {
              'visibility': 'none', // Initially hidden
              'text-field': ['get', 'name'],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 10,
              'text-offset': [0, 1.5],
              'text-anchor': 'top',
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#005AAA',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1
            }
          });
          
          // Add click event for vessels
          mapInstance.on('click', 'vessels-layer', (e) => {
            try {
              const features = mapInstance.queryRenderedFeatures(e.point, { 
                layers: ['vessels-layer'] 
              });
              
              if (features.length > 0) {
                const feature = features[0];
                const coordinates = feature.geometry.coordinates;
                
                // Find the vessel
                const vesselId = feature.properties.id;
                const vessel = VESSEL_DATA.find(v => v.id === vesselId);
                
                if (vessel) {
                  // Calculate position for popup based on mouse click
                  const point = mapInstance.project(coordinates);
                  
                  // Set selected vessel and position
                  setSelectedVessel(vessel);
                  setSelectedReport(null); // Clear any selected report
                  setSelectedTsunamiPoint(null); // Clear any selected tsunami point
                  setPopupPosition({ 
                    left: point.x, 
                    top: point.y 
                  });
                }
              }
            } catch (error) {
              console.error('Error creating vessel popup:', error);
            }
          });
          
          // Change cursor on hover for vessels
          mapInstance.on('mouseenter', 'vessels-layer', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
          });
          
          mapInstance.on('mouseleave', 'vessels-layer', () => {
            mapInstance.getCanvas().style.cursor = '';
          });
        }
      );
      
      // Add 1000 km diameter circle centered on epicenter
      const epicenter = TSUNAMI_DATA.find(data => data.event_type === 'Earthquake');
      if (epicenter) {
        try {
          // Use turf.js circle function directly in code instead of relying on a library
          // First define the center point
          const center = [epicenter.longitude, epicenter.latitude];
          
          // Function to create a circle with a given radius
          const createCircle = (radius, name, sourceId, fillId, lineId, labelId, color) => {
            // Generate the circle points (approximating with 64 points)
            const points = 64;
            
            // Create the circle by computing points along the circumference
            const circleCoordinates = [];
            for (let i = 0; i < points; i++) {
              const bearing = (i * 360) / points;
              const destination = calculateDestination(
                center[1], center[0], // lat, lng
                bearing,
                radius
              );
              circleCoordinates.push([destination[1], destination[0]]); // lng, lat
            }
            // Close the loop
            circleCoordinates.push(circleCoordinates[0]);
            
            // Create a GeoJSON feature for the circle
            const circleFeature = {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [circleCoordinates]
              },
              properties: {
                name: `${radius}km Radius`,
                radius: radius
              }
            };
            
            // Add the circle source
            mapInstance.addSource(sourceId, {
              type: 'geojson',
              data: circleFeature
            });
            
            // Add the fill layer
            mapInstance.addLayer({
              id: fillId,
              type: 'fill',
              source: sourceId,
              paint: {
                'fill-color': color,
                'fill-opacity': 0.1
              }
            });
            
            // Add the outline layer
            mapInstance.addLayer({
              id: lineId,
              type: 'line',
              source: sourceId,
              paint: {
                'line-color': color,
                'line-width': 2,
                'line-opacity': 0.6
              }
            });
            
            // Add label
            if (labelId) {
              mapInstance.addLayer({
                id: labelId,
                type: 'symbol',
                source: sourceId,
                layout: {
                  'text-field': `${radius} km radius`,
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-size': 12,
                  'text-variable-anchor': ['top-right'],
                  'text-radial-offset': 0.6,
                  'text-justify': 'auto'
                },
                paint: {
                  'text-color': color,
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1.5
                }
              });
            }
          };
          
          // Create a center point for the epicenter (used for labels)
          mapInstance.addSource('epicenter-center-source', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: center
              },
              properties: {}
            }
          });
          
          // Create circles with different radii and colors
          createCircle(250, '250km Radius', 'epicenter-radius-250-source', 
                      'epicenter-radius-250-fill', 'epicenter-radius-250-line', 
                      null, 'rgba(0, 128, 0, 0.8)'); // Green for 250km (500km diameter)
          
          createCircle(375, '375km Radius', 'epicenter-radius-375-source', 
                      'epicenter-radius-375-fill', 'epicenter-radius-375-line', 
                      null, 'rgba(255, 255, 0, 0.8)'); // Yellow for 375km (750km diameter)
          
          createCircle(500, '500km Radius', 'epicenter-radius-500-source', 
                      'epicenter-radius-500-fill', 'epicenter-radius-500-line', 
                      null, 'rgba(255, 0, 0, 0.8)'); // Red for 500km (1000km diameter)
          
          // Add time labels at specific offsets from the center for each circle
          // Create sources for label positioning at top of each circle
          const labelPositions = [
            {
              radius: 250 * 0.85, // Position inside the ring at 85% of radius
              label: '30 minutes',
              color: 'rgba(0, 128, 0, 0.8)',
              bearing: 0, // Top (North)
              sourceId: 'label-position-250'
            },
            {
              radius: 375 * 0.85, // Position inside the ring at 85% of radius
              label: '45 minutes',
              color: 'rgba(255, 255, 0, 0.8)',
              bearing: 0, // Top (North)
              sourceId: 'label-position-375'
            },
            {
              radius: 500 * 0.85, // Position inside the ring at 85% of radius
              label: '1 hour',
              color: 'rgba(255, 0, 0, 0.8)',
              bearing: 0, // Top (North)
              sourceId: 'label-position-500'
            }
          ];
          
          // Create sources for each label position
          labelPositions.forEach(pos => {
            // Calculate position at the top of each circle
            const labelCoords = calculateDestination(
              center[1], center[0],
              pos.bearing,
              pos.radius
            );
            
            // Add a source for the label position
            mapInstance.addSource(pos.sourceId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [labelCoords[1], labelCoords[0]] // lng, lat
                },
                properties: {
                  label: pos.label
                }
              }
            });
            
            // Add a label at this position
            mapInstance.addLayer({
              id: `label-${pos.radius}`,
              type: 'symbol',
              source: pos.sourceId,
              layout: {
                'text-field': pos.label,
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'text-anchor': 'center',
                'text-justify': 'center',
                'text-offset': [0, 0]
              },
              paint: {
                'text-color': pos.color,
                'text-halo-color': pos.label === '45 minutes' ? '#333333' : '#ffffff', // Dark grey background for 45 minutes only
                'text-halo-width': pos.label === '45 minutes' ? 2 : 1.5 // Slightly wider halo for 45 minutes
              }
            });
          });
          
        } catch (error) {
          console.error('Error creating circle:', error);
        }
      }
      
      // Function to calculate the destination point given a starting point, bearing and distance
      function calculateDestination(lat, lng, bearing, distance) {
        // Earth's radius in kilometers
        const R = 6371;
        
        // Convert to radians
        const lat1 = lat * Math.PI / 180;
        const lng1 = lng * Math.PI / 180;
        const brng = bearing * Math.PI / 180;
        
        // Calculate
        const d = distance / R;
        
        let lat2 = Math.asin(
          Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng)
        );
        
        let lng2 = lng1 + Math.atan2(
          Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
          Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
        );
        
        // Convert back to degrees
        lat2 = lat2 * 180 / Math.PI;
        lng2 = lng2 * 180 / Math.PI;
        
        return [lat2, lng2];
      }
      
      // Add a function to handle zoom changes and update circles
      mapInstance.on('zoomend', () => {
        updateEpicenterRadiusCircle(mapInstance);
      });
      
      // Add click event handler for tsunami points
      mapInstance.on('click', 'tsunami-points-layer', (e) => {
        // Prevent the default behavior
        e.preventDefault();
        // We're not setting any tsunami point or popup position
        // This effectively disables the popup for tsunami data points
      });
      
      // Change cursor on hover for tsunami points
      mapInstance.on('mouseenter', 'tsunami-points-layer', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      
      mapInstance.on('mouseleave', 'tsunami-points-layer', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
      
      // Add click event to show point info using our custom popup
      mapInstance.on('click', 'points-layer', (e) => {
        try {
          const features = mapInstance.queryRenderedFeatures(e.point, { 
            layers: ['points-layer'] 
          });
          
          if (features.length > 0) {
            const feature = features[0];
            const coordinates = feature.geometry.coordinates;
            
            // Find the original report data
            const reportId = feature.properties.id;
            const reportData = reports.find(r => r.id === reportId);
            
            if (reportData) {
              // Calculate position for popup based on mouse click
              const point = mapInstance.project(coordinates);
              
              // Get container bounds
              const mapBounds = mapContainer.current.getBoundingClientRect();
              
              // Set selected report and position
              setSelectedReport(reportData);
              setSelectedTsunamiPoint(null); // Clear any selected tsunami point
              setPopupPosition({ 
                left: point.x, 
                top: point.y 
              });
              
              console.log('Custom popup triggered for report:', reportId);
            }
          }
        } catch (error) {
          console.error('Error creating popup:', error);
        }
      });
      
      // Change cursor on hover
      mapInstance.on('mouseenter', 'points-layer', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      
      mapInstance.on('mouseleave', 'points-layer', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
      
      // Add cities data source
      mapInstance.addSource('cities-source', {
        type: 'geojson',
        data: createCitiesGeoJson(CITIES)
      });
      
      // Add icon for cities
      mapInstance.loadImage(
        'https://img.icons8.com/ios-filled/50/000000/city.png',
        (error, image) => {
          if (error) {
            console.error('Failed to load city icon:', error);
            return;
          }
          
          // Add the city image to the map
          mapInstance.addImage('city-icon', image);
          
          // Add a symbol layer for cities
          mapInstance.addLayer({
            id: 'cities-layer',
            type: 'symbol',
            source: 'cities-source',
            layout: {
              'icon-image': 'city-icon',
              'icon-size': 0.5,
              'icon-allow-overlap': true
            }
          });
          
          // Add a text layer for city names
          mapInstance.addLayer({
            id: 'cities-text',
            type: 'symbol',
            source: 'cities-source',
            layout: {
              'text-field': ['get', 'title'],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 10,
              'text-offset': [0, 1.5],
              'text-anchor': 'top',
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#673ab7', // Purple color for cities
              'text-halo-color': '#ffffff',
              'text-halo-width': 1
            }
          });
          
          // Remove the click event for cities - no more popups
          
          // Keep the cursor change effect
          mapInstance.on('mouseenter', 'cities-layer', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
          });
          
          mapInstance.on('mouseleave', 'cities-layer', () => {
            mapInstance.getCanvas().style.cursor = '';
          });
        }
      );
      
      // Add ports data source
      mapInstance.addSource('ports-source', {
        type: 'geojson',
        data: createPortsGeoJson(PORTS)
      });
      
      // Add icon for ports
      mapInstance.loadImage(
        'https://img.icons8.com/ios-filled/50/000000/anchor.png',
        (error, image) => {
          if (error) {
            console.error('Failed to load port icon:', error);
            return;
          }
          
          // Add the port image to the map
          mapInstance.addImage('port-icon', image);
          
          // Add a symbol layer for ports
          mapInstance.addLayer({
            id: 'ports-layer',
            type: 'symbol',
            source: 'ports-source',
            layout: {
              'icon-image': 'port-icon',
              'icon-size': 0.5,
              'icon-allow-overlap': true
            }
          });
          
          // Add a text layer for port names
          mapInstance.addLayer({
            id: 'ports-text',
            type: 'symbol',
            source: 'ports-source',
            layout: {
              'text-field': ['get', 'title'],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 10,
              'text-offset': [0, 1.5],
              'text-anchor': 'top',
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#009688', // Teal color for ports
              'text-halo-color': '#ffffff',
              'text-halo-width': 1
            }
          });
          
          // Remove the click event for ports - no more popups
          
          // Keep the cursor change effect
          mapInstance.on('mouseenter', 'ports-layer', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
          });
          
          mapInstance.on('mouseleave', 'ports-layer', () => {
            mapInstance.getCanvas().style.cursor = '';
          });
        }
      );
    });
    
    // Add error handler
    mapInstance.on('error', (e) => {
      console.error('Map error:', e);
    });
    
    // Clean up
    return () => {
      mapInstance.remove();
    };
  }, [circleRadius]); // Removed showAirports from dependency array

  

  // Handle click outside popup (separate effect for better organization)
  useEffect(() => {
    // Function to handle document clicks
    const handleClickOutside = (e) => {
      // Close popup if clicking outside both the popup and map markers
      if (
        (selectedReport || selectedTsunamiPoint || selectedVessel) && 
        !e.target.closest('.mapboxgl-popup') && 
        !e.target.closest('.popup-card') &&
        !e.defaultPrevented // Only if the click wasn't already handled
      ) {
        setSelectedReport(null);
        setSelectedTsunamiPoint(null);
        setSelectedVessel(null);
      }
    };
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedReport, selectedTsunamiPoint, selectedVessel]); // Re-run when selected items change

  // Handle sign-in button
  const handleSignIn = () => {
    requestSignIn();
  };
  
  // Format date safely
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  // Function to update the epicenter radius circle when zoom changes
  const updateEpicenterRadiusCircle = (mapInstance) => {
    if (!mapInstance || 
        !mapInstance.getSource('epicenter-radius-source') || 
        !mapInstance.getSource('epicenter-center-source')) return;
    
    try {
      // Get current zoom level
      const zoom = mapInstance.getZoom();
      console.log('Map is at zoom level:', zoom);
      
      // With our polygon-based approach, we don't need to update the circle on zoom
      // as it's automatically scaled correctly by Mapbox based on the geographic coordinates
    } catch (error) {
      console.error('Error updating epicenter radius circle:', error);
    }
  };
  
  // Convert cities data to GeoJSON format
  const createCitiesGeoJson = (citiesData) => {
    if (!citiesData || citiesData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: citiesData.map((city) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [city.location.lng, city.location.lat]
        },
        properties: {
          ...city,
          title: city.name,
          description: `City: ${city.name}`,
          isCity: true
        }
      }))
    };
  };
  
  // Convert ports data to GeoJSON format
  const createPortsGeoJson = (portsData) => {
    if (!portsData || portsData.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    
    return {
      type: 'FeatureCollection',
      features: portsData.map((port) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [port.location.lng, port.location.lat]
        },
        properties: {
          ...port,
          title: port.name,
          description: `Port: ${port.name}`,
          isPort: true
        }
      }))
    };
  };
  
  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom>
        Tsunami Reports Map
      </Typography>
      
      <Paper 
        elevation={3} 
        sx={{ 
          height: 400, 
          width: '100%', 
          mb: 3,
          position: 'relative'
        }}
      >
        {loading && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1
            }}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading reports...</Typography>
          </Box>
        )}
        
        {error && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              p: 2
            }}
          >
            <Alert severity="error" sx={{ width: '100%', maxWidth: '500px' }}>
              {error}
            </Alert>
          </Box>
        )}
        
        <div 
          id="map" 
          ref={mapContainer}
          style={{ 
            width: '100%', 
            height: '100%'
          }}
        ></div>
        
        {/* Custom Popup for Reports */}
        {selectedReport && (
          <Card
            className="popup-card"
            sx={{
              position: 'absolute',
              left: `${popupPosition.left}px`,
              top: `${popupPosition.top}px`,
              transform: 'translate(-50%, -100%)',
              width: 240,
              maxWidth: '90%',
              zIndex: 10,
              boxShadow: 3,
              mt: -2,
              fontSize: '0.85rem'
            }}
          >
            <CardContent sx={{ p: 1.5, pb: 1.5 }}>
              <Button 
                sx={{ position: 'absolute', top: 2, right: 2, minWidth: 'auto', p: 0.5 }}
                onClick={closePopup}
              >
                ×
              </Button>
              
              <Typography variant="subtitle1" sx={{ mb: 0.5, pr: 3, fontSize: '0.95rem' }}>
                {selectedReport.title || selectedReport.text || 'Unnamed Report'}
              </Typography>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">ID: </Typography>
                <Typography component="span" fontSize="inherit">{selectedReport.id.substring(0, 8)}...</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Location: </Typography>
                <Typography component="span" fontSize="inherit">
                  {selectedReport.latitude.toFixed(4)}, {selectedReport.longitude.toFixed(4)}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Description: </Typography>
                <Typography component="span" fontSize="inherit">
                  {selectedReport.description || selectedReport.text || 'None'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Reported: </Typography>
                <Typography component="span" fontSize="inherit">{formatDate(selectedReport.reported_at)}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Created: </Typography>
                <Typography component="span" fontSize="inherit">{formatDate(selectedReport.created_at)}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Severity: </Typography>
                <Typography component="span" fontSize="inherit">{selectedReport.severity || 'Unknown'}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Verified: </Typography>
                <Typography component="span" fontSize="inherit">{selectedReport.verified ? 'Yes' : 'No'}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Water Level: </Typography>
                <Typography component="span" fontSize="inherit">{selectedReport.water_level || 'Not reported'}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Wave Direction: </Typography>
                <Typography component="span" fontSize="inherit">{selectedReport.wave_direction || 'Not reported'}</Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Custom Popup for Tsunami Points */}
        {selectedTsunamiPoint && (
          <Card
            className="popup-card"
            sx={{
              position: 'absolute',
              left: `${popupPosition.left}px`,
              top: `${popupPosition.top}px`,
              transform: 'translate(-50%, -100%)',
              width: 240,
              maxWidth: '90%',
              zIndex: 10,
              boxShadow: 3,
              mt: -2,
              fontSize: '0.85rem'
            }}
          >
            <CardContent sx={{ p: 1.5, pb: 1.5 }}>
              <Button 
                sx={{ position: 'absolute', top: 2, right: 2, minWidth: 'auto', p: 0.5 }}
                onClick={closePopup}
              >
                ×
              </Button>
              
              <Typography variant="subtitle1" sx={{ mb: 0.5, pr: 3, fontSize: '0.95rem' }}>
                {selectedTsunamiPoint.location_name}
              </Typography>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Time: </Typography>
                <Typography component="span" fontSize="inherit">{selectedTsunamiPoint.timestamp}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Event Type: </Typography>
                <Typography component="span" fontSize="inherit">{selectedTsunamiPoint.event_type}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Location: </Typography>
                <Typography component="span" fontSize="inherit">
                  {selectedTsunamiPoint.latitude.toFixed(4)}, {selectedTsunamiPoint.longitude.toFixed(4)}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Wave Height: </Typography>
                <Typography component="span" fontSize="inherit">{selectedTsunamiPoint.wave_height_m} m</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Distance: </Typography>
                <Typography component="span" fontSize="inherit">{selectedTsunamiPoint.distance_km} km</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Notes: </Typography>
                <Typography component="span" fontSize="inherit">{selectedTsunamiPoint.notes}</Typography>
              </Box>
            </CardContent>
          </Card>
        )}
        
        {/* Custom Popup for Vessels */}
        {selectedVessel && (
          <Card
            className="popup-card"
            sx={{
              position: 'absolute',
              left: `${popupPosition.left}px`,
              top: `${popupPosition.top}px`,
              transform: 'translate(-50%, -100%)',
              width: 240,
              maxWidth: '90%',
              zIndex: 10,
              boxShadow: 3,
              mt: -2,
              fontSize: '0.85rem'
            }}
          >
            <CardContent sx={{ p: 1.5, pb: 1.5 }}>
              <Button 
                sx={{ position: 'absolute', top: 2, right: 2, minWidth: 'auto', p: 0.5 }}
                onClick={closePopup}
              >
                ×
              </Button>
              
              <Typography variant="subtitle1" sx={{ mb: 0.5, pr: 3, fontSize: '0.95rem' }}>
                {selectedVessel.name} ({selectedVessel.id})
              </Typography>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Status: </Typography>
                <Typography 
                  component="span" 
                  fontSize="inherit"
                  color={
                    selectedVessel.status === 'Operational' ? 'success.main' :
                    selectedVessel.status === 'Damaged' ? 'error.main' :
                    selectedVessel.status === 'Awaiting Tow' ? 'warning.main' :
                    selectedVessel.status === 'Diverted' ? 'info.main' :
                    'text.primary'
                  }
                >
                  {selectedVessel.status}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Location: </Typography>
                <Typography component="span" fontSize="inherit">
                  {selectedVessel.location.lat.toFixed(4)}, {selectedVessel.location.lng.toFixed(4)}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Last Check-In: </Typography>
                <Typography component="span" fontSize="inherit">{selectedVessel.lastCheckIn}</Typography>
              </Box>
              
              <Box sx={{ mb: 0.25, fontSize: '0.8rem' }}>
                <Typography component="span" fontWeight="bold" fontSize="inherit">Safe Harbor: </Typography>
                <Typography component="span" fontSize="inherit">{selectedVessel.safeHarbor}</Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Paper>
      
      {/* Statistics and Legend */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Total Reports: {reports.length} | Tsunami Data Points: {TSUNAMI_DATA.length} | Click on a point to see more details
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Remove the Verified Reports legend item */}
          {/* Remove the Unverified Reports legend item */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#3498db', borderRadius: '50%', mr: 1 }} />
            <Typography variant="body2">Tsunami Detection (small wave)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#e74c3c', borderRadius: '50%', mr: 1 }} />
            <Typography variant="body2">Tsunami Detection (large wave)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 3, bgcolor: 'rgba(0, 128, 0, 0.8)', mr: 1, border: '1px solid rgba(0, 128, 0, 1)' }} />
            <Typography variant="body2">250 km / 30 minutes</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 3, bgcolor: 'rgba(255, 255, 0, 0.8)', mr: 1, border: '1px solid rgba(255, 255, 0, 1)' }} />
            <Typography variant="body2">375 km / 45 minutes</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 3, bgcolor: 'rgba(255, 0, 0, 0.8)', mr: 1, border: '1px solid rgba(255, 0, 0, 1)' }} />
            <Typography variant="body2">500 km / 1 hour</Typography>
          </Box>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Map Controls
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Go to Predefined Location:
          </Typography>
          <ButtonGroup variant="outlined" size="small">
            {Object.entries(LOCATIONS).map(([key, location]) => (
              <Button 
                key={key}
                onClick={() => flyToPredefinedLocation(key)}
              >
                {location.label}
              </Button>
            ))}
            <Button
              onClick={flyToTsunamiEpicenter}
              color="secondary"
            >
              Tsunami Epicenter
            </Button>
          </ButtonGroup>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* <Typography variant="subtitle2" sx={{ width: '100%' }}>
          Set Circle Position:
        </Typography>
        <TextField
          label="Circle Latitude"
          type="number"
          size="small"
          value={circleLatitude}
          onChange={(e) => setCircleLatitude(e.target.value)}
          placeholder="e.g., 51.5074"
          inputProps={{ step: 'any' }}
        />
        <TextField
          label="Circle Longitude"
          type="number"
          size="small"
          value={circleLongitude}
          onChange={(e) => setCircleLongitude(e.target.value)}
          placeholder="e.g., -0.1278"
          inputProps={{ step: 'any' }}
        />
        <TextField
          label="Zoom"
          type="number"
          size="small"
          value={customZoom}
          onChange={(e) => setCustomZoom(e.target.value)}
          inputProps={{ min: 0, max: 22, step: 0.1 }}
          sx={{ width: 100 }}
        />
        <TextField
        label="Circle Radius (px)"
        type="number"
        size="small"
        value={circleRadius}
        onChange={(e) => setCircleRadius(e.target.value)}
        inputProps={{ min: 1, max: 200, step: 1 }}
        sx={{ width: 150 }}
        />
        <Button variant="contained" onClick={updateCirclePosition}>
          Update Circle Position
        </Button> */}
        </Box>
        
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ width: '100%' }}>
            Go to Custom Location:
          </Typography>
          <TextField
            label="Latitude"
            type="number"
            size="small"
            value={customLat}
            onChange={(e) => setCustomLat(e.target.value)}
            placeholder="e.g., 51.5074"
            inputProps={{ step: 'any' }}
          />
          <TextField
            label="Longitude"
            type="number"
            size="small"
            value={customLng}
            onChange={(e) => setCustomLng(e.target.value)}
            placeholder="e.g., -0.1278"
            inputProps={{ step: 'any' }}
          />
          <TextField
            label="Zoom"
            type="number"
            size="small"
            value={customZoom}
            onChange={(e) => setCustomZoom(e.target.value)}
            inputProps={{ min: 0, max: 22, step: 0.1 }}
            sx={{ width: 100 }}
          />
          <Button 
            variant="contained" 
            onClick={flyToCustomLocation}
            disabled={!customLat || !customLng}
          >
            Go to Location
          </Button>
          <Button variant="contained" onClick={refreshMapData}>
            Refresh
          </Button>
          {/* Removed vessels toggle button */}
          <Button
            variant="contained"
            color={showAirports ? "secondary" : "primary"}
            onClick={toggleAirports}
          >
            {showAirports ? "Hide Airports" : "Show Airports"}
          </Button>
        </Box>
      </Paper>
    </div>
  );
}

export default SimpleMapTest; 